import { toRelativeUrl } from "@okta/okta-auth-js";
import { fetchBaseQuery } from "@reduxjs/toolkit/query";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { REDIRECT_INDICATOR } from "@/api/constants/authRedirect";
import { getEmbedAccessToken, getEmbedApiBaseUrl, isEmbedAuthEnabled } from "@/api/embedAuth";
import { getExternalIdpForCurrentLocation } from "@/auth/idpRouting";
import { getRealEstateIdAndSaveToCookie } from "@/helpers/valuationReportCookieHelpers";
import { oktaAuth } from "@/oktaAuth";

// Global abort controller to cancel valuation report requests on 401
let valuationReportAbortController: AbortController | null = null;

const normalizeBaseUrl = (baseUrl: string): string => baseUrl.replace(/\/$/, "");

const withBaseUrl = (args: string | FetchArgs, baseUrl: string): string | FetchArgs => {
  const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
  if (!normalizedBaseUrl) return args;

  if (typeof args === "string") {
    return `${normalizedBaseUrl}${args.startsWith("/") ? args : `/${args}`}`;
  }

  return {
    ...args,
    url: `${normalizedBaseUrl}${args.url.startsWith("/") ? args.url : `/${args.url}`}`,
  };
};

const getSprengConnectApiBaseUrl = (): string => getEmbedApiBaseUrl() ?? window.REACT_APP_SPRENGCONNECT_API;

const getGeneratedApiBaseUrl = (): string => {
  const baseUrl = getSprengConnectApiBaseUrl();
  return baseUrl.includes(".de/") ? baseUrl.split(".de/")[0] + ".de/" : baseUrl;
};

const getAccessToken = async (): Promise<string | null> => {
  const embedAccessToken = getEmbedAccessToken();

  if (embedAccessToken) {
    return embedAccessToken;
  }

  return oktaAuth.getAccessToken() ?? null;
};

const prepareAuthorizedHeaders = async (headers: Headers): Promise<Headers> => {
  const authToken = await getAccessToken();

  if (authToken) {
    headers.set("Authorization", "Bearer " + authToken);
  }

  return headers;
};

const sprengConnectRawBaseQuery = fetchBaseQuery({
  prepareHeaders: prepareAuthorizedHeaders,
});

export const sprengConnectApiBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
  args,
  api,
  extraOptions,
) => sprengConnectRawBaseQuery(withBaseUrl(args, getSprengConnectApiBaseUrl()), api, extraOptions);

const createGeneratedApiBaseQueryWithValuationReportRedirect = (): BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> => {
  const baseQuery = fetchBaseQuery({
    prepareHeaders: (headers, { endpoint }) => {
      const endpointName = endpoint || "";
      const isValuationReportEndpoint = String(endpointName).includes("valuation_report");

      if (!isValuationReportEndpoint || isEmbedAuthEnabled()) {
        return prepareAuthorizedHeaders(headers);
      }

      return headers;
    },
  });

  return async (args, api, extraOptions) => {
    const url = typeof args === "string" ? args : args.url;
    const isValuationReportRequest = url && url.includes("/valuation_report");

    if (isValuationReportRequest) {
      if (!valuationReportAbortController) {
        valuationReportAbortController = new AbortController();
      }

      const argsWithSignal =
        typeof args === "string"
          ? { url: args, signal: valuationReportAbortController.signal }
          : { ...args, signal: valuationReportAbortController.signal };

      const result = await baseQuery(withBaseUrl(argsWithSignal, getGeneratedApiBaseUrl()), api, extraOptions);

      if (result.error && result.error.status === 401) {
        if (isEmbedAuthEnabled()) {
          return result;
        }
        const realEstateId = getRealEstateIdAndSaveToCookie();

        if (!realEstateId) {
          console.error("No realEstateId found. Cannot proceed with valuation report login.");
          return result;
        }

        try {
          // Cancel all other valuation report requests
          if (valuationReportAbortController) {
            valuationReportAbortController.abort();
            valuationReportAbortController = null;
          }

          const baseUrl = getGeneratedApiBaseUrl();
          const loginUrl = `${baseUrl}service/api/valuation_report/login?realEstateId=${realEstateId}`;

          window.location.href = loginUrl;

          // Return success to prevent RTK Query from processing the original 401
          return { data: { redirectType: REDIRECT_INDICATOR } };
        } catch (loginError) {
          console.error(" Exception during redirect:", loginError);
          return result;
        }
      }

      return result;
    }

    // Handle 401 for all other endpoints
    const result = await baseQuery(withBaseUrl(args, getGeneratedApiBaseUrl()), api, extraOptions);
    if (result.error && result.error.status === 401 && !isEmbedAuthEnabled()) {
      try {
        const idp = getExternalIdpForCurrentLocation();
        const originalUri = toRelativeUrl(window.location.href, window.location.origin);
        await oktaAuth.signInWithRedirect({ originalUri, ...(idp ? { idp } : {}) });
      } catch (error) {
        console.error("Unable to restart login:", error);
      }
    }
    return result;
  };
};

export const generatedApiBaseQuery = createGeneratedApiBaseQueryWithValuationReportRedirect();
